import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine } from 'recharts';
import useIsMobile from '../hooks/useIsMobile';

// Revenue Manager — Revenue Dashboard tab.
//
// Every other RMS tab (Decisions/Impact/Automation/Audit) is a table — this is the
// screen built to be shown TO a hotelier: a trend line (actual vs forecast vs last
// year), a demand heatmap, the Revenue Impact number as a chart, and rate positioning
// vs the market — the visual language IDeaS/Duetto/RateGain lead their dashboards
// with, assembled entirely from data this suite already collects elsewhere:
//   - actuals:   pms_night_audit_snapshot via /api/v1/night-audit/snapshot/range
//   - forecast:  ai_occupancy_forecast via /api/v1/ai/occupancy-forecast/calendar-overlay
//   - impact:    rms_rate_decision + bookings via /api/v1/rms/revenue-impact
//   - rate pos.: Rate Shopper via /api/v1/rate-shopper/rates/compare
// Each source is fetched independently (Promise.allSettled) so one missing/forbidden
// source (e.g. a role without night-audit access, or Occupancy Forecast not enabled)
// degrades that one card gracefully instead of blanking the whole dashboard.

const fmtINR = (n) => (n == null ? '—' : `₹${Math.round(Number(n)).toLocaleString('en-IN')}`);
const fmtPct = (n) => (n == null ? '—' : `${Number(n).toFixed(1)}%`);
const num = (v) => (v == null || v === '' ? null : Number(v));
const avg = (arr) => { const v = arr.filter((n) => n != null); return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null; };

function isoToday(offset = 0) { return new Date(Date.now() + offset * 86400000).toISOString().slice(0, 10); }
function addDays(iso, days) { return new Date(new Date(`${iso}T00:00:00Z`).getTime() + days * 86400000).toISOString().slice(0, 10); }
function dayOffset(iso) { return Math.round((new Date(`${iso}T00:00:00Z`) - new Date(`${isoToday(0)}T00:00:00Z`)) / 86400000); }
function dateLabel(offset) { return new Date(`${addDays(isoToday(0), offset)}T00:00:00Z`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }); }
function weekStart(iso) { const d = new Date(`${iso}T00:00:00Z`); const day = d.getUTCDay(); const diff = (day === 0 ? -6 : 1) - day; return new Date(d.getTime() + diff * 86400000).toISOString().slice(0, 10); }

const card = { background: 'var(--theme-surface, #fff)', border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 12, padding: 16 };
const kpiBox = { flex: '1 1 160px', minWidth: 160, background: 'var(--theme-surface, #fff)', border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 12, padding: '12px 14px' };
const pill = (active) => ({
  padding: '5px 13px', borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: 'pointer',
  border: active ? 'none' : '1px solid var(--theme-border, #e5e7eb)',
  background: active ? 'var(--theme-primary, #0d8f83)' : 'var(--theme-surface, #fff)',
  color: active ? '#fff' : 'var(--theme-text-muted, #64748b)',
});
const cardTitle = { fontWeight: 700, fontSize: 14, marginBottom: 2 };
const cardSub = { fontSize: 12, color: 'var(--theme-text-muted)', marginBottom: 12 };
const skeleton = { height: 260, borderRadius: 8, background: 'linear-gradient(90deg, var(--theme-surface-2, #f1f5f9) 25%, var(--theme-border-light, #f8fafc) 37%, var(--theme-surface-2, #f1f5f9) 63%)', backgroundSize: '400% 100%', animation: 'rms-shimmer 1.4s ease infinite' };
const emptyBox = { padding: 24, background: 'var(--theme-surface-2, #f1f5f9)', borderRadius: 8, color: 'var(--theme-text-muted)', textAlign: 'center', fontSize: 13 };

const METRICS = {
  revpar: { label: 'RevPAR', fmt: fmtINR, actualKey: 'actual_revpar', lyKey: 'ly_revpar', fcKey: 'forecast_revpar' },
  adr: { label: 'ADR', fmt: fmtINR, actualKey: 'actual_adr', lyKey: 'ly_adr', fcKey: 'forecast_adr' },
  occupancy: { label: 'Occupancy %', fmt: fmtPct, actualKey: 'actual_occupancy', lyKey: 'ly_occupancy', fcKey: 'forecast_occupancy' },
};

const TIERS = [
  { max: -1, bg: 'var(--theme-surface-2, #f1f5f9)', label: 'No data' },
  { max: 50, bg: '#fecaca', label: '<50%' },
  { max: 70, bg: '#fde68a', label: '50–70%' },
  { max: 85, bg: '#86efac', label: '70–85%' },
  { max: 1000, bg: '#059669', label: '85%+' },
];
function tierFor(pct) { if (pct == null) return TIERS[0]; for (const t of TIERS) if (pct <= t.max) return t; return TIERS[TIERS.length - 1]; }

function RmsRevenueDashboard({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const [metric, setMetric] = useState('revpar');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [actualsThisYear, setActualsThisYear] = useState(null);
  const [actualsLastYear, setActualsLastYear] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [rateCompare, setRateCompare] = useState(null);
  const [impact, setImpact] = useState(null);

  const fetchJson = useCallback(async (path) => {
    const res = await fetch(path, { headers: { Authorization: `Bearer ${token}`, 'x-chain-id': selectedProperty?.chain_id || '', 'x-property-id': selectedProperty?.id || '' } });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
    return data;
  }, [token, selectedProperty]);

  useEffect(() => {
    if (!selectedProperty?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setErrors({});
      const from = isoToday(-90), to = isoToday(89);
      const lyFrom = addDays(from, -364), lyTo = addDays(to, -364);
      const sources = [
        ['actualsThisYear', `/api/v1/night-audit/snapshot/range?from=${from}&to=${isoToday(-1)}`, setActualsThisYear],
        ['actualsLastYear', `/api/v1/night-audit/snapshot/range?from=${lyFrom}&to=${lyTo}`, setActualsLastYear],
        ['forecast', `/api/v1/ai/occupancy-forecast/calendar-overlay?from=${isoToday(0)}&to=${to}`, setForecast],
        ['rateCompare', `/api/v1/rate-shopper/rates/compare?meal=EP&from=${isoToday(0)}&to=${to}`, setRateCompare],
        ['impact', `/api/v1/rms/revenue-impact?from=${from}&to=${to}`, setImpact],
      ];
      const results = await Promise.allSettled(sources.map(([, path]) => fetchJson(path)));
      if (cancelled) return;
      const errs = {};
      results.forEach((r, i) => {
        const [key, , setter] = sources[i];
        if (r.status === 'fulfilled') setter(r.value); else { setter(null); errs[key] = r.reason?.message || 'Failed to load'; }
      });
      setErrors(errs); setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [selectedProperty, fetchJson]);

  // ── Trend: actual vs last-year vs forecast, keyed by day-offset from today ──
  const trendData = useMemo(() => {
    const map = new Map();
    const put = (rows, offsetFn, mapping) => {
      for (const r of rows || []) {
        const off = offsetFn(r);
        if (off == null || Number.isNaN(off)) continue;
        map.set(off, { ...(map.get(off) || { offset: off }), ...mapping(r) });
      }
    };
    put(actualsThisYear, (r) => dayOffset(r.business_date), (r) => ({ actual_occupancy: num(r.occupancy_percent), actual_adr: num(r.adr), actual_revpar: num(r.revpar) }));
    put(actualsLastYear, (r) => dayOffset(addDays(r.business_date, 364)), (r) => ({ ly_occupancy: num(r.occupancy_percent), ly_adr: num(r.adr), ly_revpar: num(r.revpar) }));
    put(forecast?.forecasts, (r) => dayOffset(r.forecast_date), (r) => ({ forecast_occupancy: num(r.projected_occupancy), forecast_adr: num(r.projected_adr), forecast_revpar: num(r.projected_revpar) }));
    return [...map.values()].sort((a, b) => a.offset - b.offset);
  }, [actualsThisYear, actualsLastYear, forecast]);

  // ── Heatmap: next ~13 weeks, actual where it exists (past), forecast beyond ──
  const heatWeeks = useMemo(() => {
    const byDate = new Map();
    for (const r of actualsThisYear || []) byDate.set(r.business_date, { occ: num(r.occupancy_percent), adr: num(r.adr), revpar: num(r.revpar), source: 'Actual' });
    for (const r of forecast?.forecasts || []) if (!byDate.has(r.forecast_date)) byDate.set(r.forecast_date, { occ: num(r.projected_occupancy), adr: num(r.projected_adr), revpar: num(r.projected_revpar), source: 'Forecast' });
    const start = isoToday(-7), end = isoToday(83);
    const cells = [];
    for (let d = start; d <= end; d = addDays(d, 1)) cells.push({ date: d, ...(byDate.get(d) || {}) });
    if (!cells.length) return [];
    const leadPad = new Date(`${cells[0].date}T00:00:00Z`).getUTCDay();
    const padded = [...Array(leadPad).fill(null), ...cells];
    const rows = [];
    for (let i = 0; i < padded.length; i += 7) rows.push(padded.slice(i, i + 7));
    return rows;
  }, [actualsThisYear, forecast]);

  // ── Revenue Impact by week ──
  const impactByWeek = useMemo(() => {
    const map = new Map();
    for (const r of impact?.rows || []) {
      const wk = weekStart(r.stay_date);
      const cur = map.get(wk) || { week: wk, impact: 0 };
      cur.impact += Number(r.impact || 0);
      map.set(wk, cur);
    }
    return [...map.values()].sort((a, b) => a.week.localeCompare(b.week));
  }, [impact]);

  // ── Rate positioning: own vs market median, averaged across mapped rooms/date ──
  const rateTrend = useMemo(() => {
    const map = new Map();
    for (const r of rateCompare?.rows || []) {
      const cur = map.get(r.stay_date) || { stay_date: r.stay_date, ownSum: 0, ownN: 0, mktSum: 0, mktN: 0 };
      if (r.own_rate != null) { cur.ownSum += Number(r.own_rate); cur.ownN += 1; }
      if (r.market_median != null) { cur.mktSum += Number(r.market_median); cur.mktN += 1; }
      map.set(r.stay_date, cur);
    }
    return [...map.values()]
      .map((c) => ({ offset: dayOffset(c.stay_date), own_rate: c.ownN ? c.ownSum / c.ownN : null, market_median: c.mktN ? c.mktSum / c.mktN : null }))
      .sort((a, b) => a.offset - b.offset);
  }, [rateCompare]);

  // ── KPI strip ──
  const kpis = useMemo(() => {
    const latest = actualsThisYear?.length ? actualsThisYear[actualsThisYear.length - 1] : null;
    const fcAvgRevpar = avg((forecast?.forecasts || []).filter((f) => dayOffset(f.forecast_date) >= 0 && dayOffset(f.forecast_date) < 30).map((f) => num(f.projected_revpar)));
    const lyAvgRevpar = avg((actualsLastYear || []).filter((r) => { const off = dayOffset(addDays(r.business_date, 364)); return off >= 0 && off < 30; }).map((r) => num(r.revpar)));
    const fcLift = fcAvgRevpar != null && lyAvgRevpar ? ((fcAvgRevpar - lyAvgRevpar) / lyAvgRevpar) * 100 : null;
    const latestRate = rateTrend.filter((r) => r.offset >= 0)[0] || null;
    return {
      latestOccupancy: latest ? num(latest.occupancy_percent) : null,
      latestRevpar: latest ? num(latest.revpar) : null,
      latestDate: latest?.business_date || null,
      fcAvgRevpar, fcLift,
      totalImpact: impact?.kpis?.total_impact ?? null,
      latestRate,
    };
  }, [actualsThisYear, actualsLastYear, forecast, impact, rateTrend]);

  const m = METRICS[metric];

  if (!selectedProperty?.id) return null;

  return (
    <div>
      <style>{'@keyframes rms-shimmer { 0% { background-position: 100% 0; } 100% { background-position: 0 0; } }'}</style>

      {/* KPI strip */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={kpiBox}>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{kpis.latestOccupancy != null ? fmtPct(kpis.latestOccupancy) : '—'}</div>
          <div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Occupancy{kpis.latestDate ? ` · ${kpis.latestDate}` : ' (latest audit)'}</div>
        </div>
        <div style={kpiBox}>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{fmtINR(kpis.latestRevpar)}</div>
          <div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>RevPAR{kpis.latestDate ? ` · ${kpis.latestDate}` : ' (latest audit)'}</div>
        </div>
        <div style={kpiBox}>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{fmtINR(kpis.fcAvgRevpar)}</div>
          <div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>
            Next 30d forecast RevPAR{kpis.fcLift != null && <span style={{ color: kpis.fcLift >= 0 ? '#047857' : '#b91c1c', fontWeight: 700 }}> ({kpis.fcLift > 0 ? '+' : ''}{kpis.fcLift.toFixed(1)}% vs LY)</span>}
          </div>
        </div>
        <div style={kpiBox}>
          <div style={{ fontSize: 22, fontWeight: 700, color: kpis.totalImpact > 0 ? '#047857' : kpis.totalImpact < 0 ? '#b91c1c' : undefined }}>{kpis.totalImpact != null ? `${kpis.totalImpact > 0 ? '+' : ''}${fmtINR(kpis.totalImpact)}` : '—'}</div>
          <div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>RMS revenue impact (window)</div>
        </div>
        <div style={kpiBox}>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{kpis.latestRate?.own_rate != null ? fmtINR(kpis.latestRate.own_rate) : '—'}</div>
          <div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Own rate today vs market {kpis.latestRate?.market_median != null ? fmtINR(kpis.latestRate.market_median) : '—'}</div>
        </div>
      </div>

      <div style={{ display: 'grid', gap: 16 }}>
        {/* Trend chart */}
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <div><div style={cardTitle}>Performance trend</div><div style={cardSub}>Actual vs last year vs AI forecast — 90 days back, 90 days forward.</div></div>
            <div style={{ display: 'flex', gap: 6 }}>
              {Object.entries(METRICS).map(([k, mm]) => <button key={k} onClick={() => setMetric(k)} style={pill(metric === k)}>{mm.label}</button>)}
            </div>
          </div>
          {loading ? <div style={skeleton} /> : errors.actualsThisYear && errors.forecast ? (
            <div style={emptyBox}>Couldn't load actuals or forecast for this property. {errors.actualsThisYear || errors.forecast}</div>
          ) : trendData.length === 0 ? <div style={emptyBox}>No actuals or forecast data yet for this window.</div> : (
            <div style={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <LineChart data={trendData} margin={{ top: 5, right: 20, bottom: 5, left: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--theme-border, #e5e7eb)" />
                  <XAxis dataKey="offset" tickFormatter={dateLabel} tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={48} tickFormatter={(v) => (metric === 'occupancy' ? `${v}%` : `₹${v}`)} />
                  <Tooltip labelFormatter={dateLabel} formatter={(v, name) => [m.fmt(v), name]} />
                  <Legend />
                  <ReferenceLine x={0} stroke="#94a3b8" strokeDasharray="2 2" label={{ value: 'Today', fontSize: 10, fill: '#94a3b8' }} />
                  <Line type="monotone" dataKey={m.actualKey} name="Actual" stroke="#2563eb" strokeWidth={2} dot={false} connectNulls />
                  <Line type="monotone" dataKey={m.lyKey} name="Last Year" stroke="#94a3b8" strokeDasharray="4 4" strokeWidth={2} dot={false} connectNulls />
                  <Line type="monotone" dataKey={m.fcKey} name="Forecast" stroke="#059669" strokeDasharray="2 2" strokeWidth={2} dot={false} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          {forecast?.enabled === false && <div style={{ fontSize: 12, color: 'var(--theme-text-muted)', marginTop: 6 }}>ℹ Occupancy Forecast isn't enabled for this property — showing actual/last-year only.</div>}
        </div>

        {/* Demand heatmap */}
        <div style={card}>
          <div style={cardTitle}>Demand calendar</div>
          <div style={cardSub}>Occupancy by day — actual for dates that have passed, AI forecast beyond today.</div>
          {loading ? <div style={{ ...skeleton, height: 180 }} /> : heatWeeks.length === 0 ? <div style={emptyBox}>No occupancy data yet.</div> : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <div style={{ minWidth: 420 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3, marginBottom: 4 }}>
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <div key={i} style={{ fontSize: 10, color: 'var(--theme-text-muted)', textAlign: 'center' }}>{d}</div>)}
                  </div>
                  {heatWeeks.map((week, wi) => (
                    <div key={wi} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3, marginBottom: 3 }}>
                      {week.map((c, ci) => c == null ? <div key={ci} /> : (
                        <div key={ci}
                          title={`${c.date}${c.occ != null ? ` · ${fmtPct(c.occ)} occ` : ' · no data'}${c.adr != null ? ` · ADR ${fmtINR(c.adr)}` : ''}${c.revpar != null ? ` · RevPAR ${fmtINR(c.revpar)}` : ''}${c.source ? ` · ${c.source}` : ''}`}
                          style={{ aspectRatio: '1', minHeight: 22, borderRadius: 4, background: tierFor(c.occ).bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: c.occ != null && c.occ > 70 ? '#fff' : 'var(--theme-text-muted)', cursor: 'default' }}>
                          {new Date(`${c.date}T00:00:00Z`).getUTCDate()}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
                {TIERS.map((t) => <div key={t.label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--theme-text-muted)' }}><span style={{ width: 12, height: 12, borderRadius: 3, background: t.bg, display: 'inline-block' }} />{t.label}</div>)}
              </div>
            </>
          )}
        </div>

        {/* Impact + rate positioning side by side */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ ...card, flex: '1 1 380px', minWidth: isMobile ? 0 : 320 }}>
            <div style={cardTitle}>Revenue impact by week</div>
            <div style={cardSub}>From the Revenue Impact tab — rooms booked after each applied RMS rate change, valued at the new rate vs. the prior rate.</div>
            {loading ? <div style={{ ...skeleton, height: 220 }} /> : errors.impact ? <div style={emptyBox}>{errors.impact}</div> : impactByWeek.length === 0 ? <div style={emptyBox}>No applied RMS rate changes with bookings yet.</div> : (
              <div style={{ width: '100%', height: 240 }}>
                <ResponsiveContainer>
                  <BarChart data={impactByWeek} margin={{ top: 5, right: 10, bottom: 5, left: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--theme-border, #e5e7eb)" />
                    <XAxis dataKey="week" tick={{ fontSize: 10 }} tickFormatter={(d) => new Date(`${d}T00:00:00Z`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} />
                    <YAxis tick={{ fontSize: 11 }} width={56} tickFormatter={(v) => `₹${Math.round(v / 1000)}k`} />
                    <Tooltip labelFormatter={(d) => `Week of ${d}`} formatter={(v) => [fmtINR(v), 'Impact']} />
                    <ReferenceLine y={0} stroke="#94a3b8" />
                    <Bar dataKey="impact" radius={[3, 3, 0, 0]}>
                      {impactByWeek.map((row, i) => <Cell key={i} fill={row.impact >= 0 ? '#059669' : '#dc2626'} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div style={{ ...card, flex: '1 1 380px', minWidth: isMobile ? 0 : 320 }}>
            <div style={cardTitle}>Rate positioning</div>
            <div style={cardSub}>Own rate vs. market median (Rate Shopper), averaged across mapped rooms per date.</div>
            {loading ? <div style={{ ...skeleton, height: 220 }} /> : errors.rateCompare ? <div style={emptyBox}>{errors.rateCompare}</div> : rateTrend.length === 0 ? <div style={emptyBox}>No competitor rate snapshots yet.</div> : (
              <div style={{ width: '100%', height: 240 }}>
                <ResponsiveContainer>
                  <LineChart data={rateTrend} margin={{ top: 5, right: 10, bottom: 5, left: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--theme-border, #e5e7eb)" />
                    <XAxis dataKey="offset" tickFormatter={dateLabel} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} width={48} tickFormatter={(v) => `₹${v}`} />
                    <Tooltip labelFormatter={dateLabel} formatter={(v, name) => [fmtINR(v), name]} />
                    <Legend />
                    <Line type="monotone" dataKey="own_rate" name="Own rate" stroke="#7c3aed" strokeWidth={2} dot={false} connectNulls />
                    <Line type="monotone" dataKey="market_median" name="Market median" stroke="#f59e0b" strokeDasharray="4 4" strokeWidth={2} dot={false} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default RmsRevenueDashboard;
